import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, connect, useSelector } from 'react-redux';
import { ClickableTile, Button, InlineLoading } from 'carbon-components-react';
import { ChevronRight16 } from '@carbon/icons-react';
import { CFCProps, SCREEN_SIZE } from 'types';
import { fetchTopThreeCLO } from 'redux/clo';
import { RootState } from 'redux/root.reducer';
import {
  selectTopThreeCLO,
  selectLoadingTopThreeCLO,
  selectRefreshTopThreeCLO
} from 'redux/clo/selectors';
import { selectShowCLOListing } from 'redux/app/selectors';
import { selectUser } from 'redux/user/selector';
import { selectIsAuthenticated } from 'redux/session/selectors';

const mapStateToProps = ({ cloCache }: RootState) => ({
  topThreeCLO: cloCache?.topThreeCLO,
  isLoadingTopThreeCLO: cloCache?.loadingTopThreeCLO
});
const connector = connect(mapStateToProps);

type OwnProps = {
  screenSize: String;
  handleCLOTileClick: Function;
  handleSeeALLCLOButtonClick: Function;
  enquiryDetail: any;
  setQuerySeed: Function;
  querySeed: string;
  isModalOpen: any;
  currentView: any;
};

type Props = CFCProps<typeof connector> & OwnProps;

const CLOModalSection: React.FC<Props | any> = (props: Props) => {
  const {
    screenSize,
    handleCLOTileClick,
    handleSeeALLCLOButtonClick,
    enquiryDetail,
    setQuerySeed,
    querySeed,
    isModalOpen,
    currentView
  }: any = props;
  const dispatch = useDispatch();
  const topThreeCLO = useSelector(selectTopThreeCLO);
  const loadingTopThreeCLO = useSelector(selectLoadingTopThreeCLO);
  const refreshTopThreeCLO = useSelector(selectRefreshTopThreeCLO);
  const showCLOListing = useSelector(selectShowCLOListing);
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [showCLO, setShowCLO] = useState<boolean>(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<boolean>(false);

  const getTop3CLO = useCallback(
    (params: any) => {
      dispatch(fetchTopThreeCLO(params));
    },
    [dispatch]
  );

  useEffect(() => {
    if (isModalOpen && isAuthenticated) {
      const params = {
        filter: 'top3:true,withHidden:false',
        limit: 10,
        offset: 0,
        include: '',
        extent: enquiryDetail.shape,
        fields: 'id,name,details',
        orderBy: '',
        seed: querySeed
      };

      if (refreshTopThreeCLO) {
        getTop3CLO(params);
      }
      setModalBoxStatus(true);
    } else {
      setModalBoxStatus(false);
    }
  }, [
    isModalOpen,
    currentView,
    enquiryDetail,
    getTop3CLO,
    querySeed,
    refreshTopThreeCLO,
    isAuthenticated
  ]);

  useEffect(() => {
    if (showCLOListing && user) {
      let showFlag = showCLOListing?.[user?.id];
      if (showCLOListing?.[user?.id] === undefined) {
        showFlag = true;
      }
      setShowCLO(showFlag);
    } else if (showCLOListing === undefined) {
      setShowCLO(true);
    }
  }, [user, showCLOListing]);

  const handleTileClick = (cloId: number) => {
    const cloListInfo = topThreeCLO.Info;
    !querySeed && setQuerySeed(cloListInfo.seed);
    handleCLOTileClick(cloId);
  };

  const handleSeeAllCLOClick = () => {
    const cloListInfo = topThreeCLO.Info;
    !querySeed && setQuerySeed(cloListInfo.seed);
    handleSeeALLCLOButtonClick();
  };

  const CLImgName = 'certloc';

  return (
    <>
      <div className="visible-height"></div>
      <div className="clo-section old-version">
        <div className="engage-certified-locator">
          <div className="static-content">
            <div className="content">
              <img
                src={`../${CLImgName}.png`}
                srcSet={`../${CLImgName}.png, ../${CLImgName}@2x.png 2x, ../${CLImgName}@3x.png 3x`}
                alt="certloc certifications"
                className="certified-locator-icon"
              />
            </div>
            <div className="text">
              Underground infrastructure networks are complex and require expert
              knowledge to identify the location of buried services carefully
              and safely. To reduce the risk of accidental damage or workplace
              injury, we highly recommend the services of a Certified Locator.
              The Certified Locator program is owned and operated by
              <a
                href="https://certloc.com.au/locators"
                target="_blank"
                rel="noopener noreferrer"
              >
                CERTLOC Global
              </a>
              .
            </div>
          </div>
          {showCLO ? (
            <div className="certified-locators-listing">
              <div>
                <div className="header">
                  <div className="title">
                    Certified Locating Organisations servicing this job site (in
                    random order)
                  </div>
                </div>
                {!loadingTopThreeCLO && modalBoxStatus ? (
                  <>
                    <div className="listing-container">
                      {topThreeCLO?.LocatingOrganisations?.map((clo: any) => {
                        return (
                          <ClickableTile
                            key={clo.id}
                            className="locator-tile"
                            handleClick={() => handleTileClick(clo.id)}
                          >
                            <div className="locator-tile-name">{clo.name}</div>
                            <div className="locator-tile-desc">
                              {`${clo.details.locatorAmount} Certified Locator${
                                clo.details.locatorAmount > 1 ? 's' : ''
                              }`}
                            </div>
                            <div className="locator-tile-footer">
                              <ChevronRight16 />
                            </div>
                          </ClickableTile>
                        );
                      })}
                    </div>

                    <div className="all-locators-button">
                      {screenSize !== SCREEN_SIZE.SM && (
                        <Button
                          kind="tertiary"
                          renderIcon={ChevronRight16}
                          onClick={handleSeeAllCLOClick}
                          className="btn-custom"
                        >
                          See all Certified Locating Organisations servicing
                          your job site
                        </Button>
                      )}

                      {screenSize === SCREEN_SIZE.SM && (
                        <div
                          className="btn-all-locators-sm"
                          onClick={handleSeeAllCLOClick}
                        >
                          <div className="btn-text">
                            See all Certified Locating Organisations servicing
                            this area
                          </div>
                          <Button hasIconOnly renderIcon={ChevronRight16} />
                        </div>
                      )}
                    </div>
                    <div className="text">
                      <span>About this list.</span>
                      When you lodge an enquiry, three Certified Locating
                      Organisations (CLOs) servicing your dig site are shown
                      here. These CLOs are selected at random to ensure that all
                      CLOs get the same opportunity to appear. You can also
                      access the full list with the &quot;See all ...&quot;
                      button.
                    </div>
                  </>
                ) : (
                  <div className="listing-container">
                    {['', '', ''].map((ele: any, idx: number) => {
                      return (
                        <ClickableTile key={idx} className="locator-tile">
                          <div className="locator-tile-name">
                            {<InlineLoading description="Loading" />}
                          </div>
                          <div className="locator-tile-footer">
                            <ChevronRight16 />
                          </div>
                        </ClickableTile>
                      );
                    })}
                  </div>
                )}
                {!loadingTopThreeCLO &&
                  topThreeCLO?.LocatingOrganisations?.length <= 0 && (
                    <div className="no-result">
                      No Certified Locating Organisations to display
                    </div>
                  )}
              </div>
            </div>
          ) : (
            ''
          )}
          <div className="why-use-certified-locator">
            <div className="title">Why use a Certified Locator?</div>
            <div className="paragraph">
              It&apos;s highly recommended to engage a Certified Locating
              Organisation/Certified Locator to help prevent accidental damage.
              The use of Certified Locators is required by some major utilities,
              such as Telstra.
            </div>
            <div className="paragraph">
              Certified Locators are nationally recognised for meeting the
              minimum industry standard for locating excellence, best practice
              and safety. They are skilled in interpreting utility plans,
              diligently investigating a site, and navigating complex utility
              networks, to locate and identify the underground utilities present
              at your worksite.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CLOModalSection;
